import type { ChangeEvent, FormEvent } from 'react'
import type { RequestType } from '@/types'
import type { RequestErrors, RequestField, RequestValues } from '@/lib/requests/validation'

import { useCallback, useEffect, useState } from 'react'
import { emptyRequestValues, validateRequest } from '@/lib/requests/validation'

export type RequestFormStatus = 'idle' | 'submitting' | 'success' | 'error'

interface UseRequestFormOptions {
    type: RequestType
    formId: string
    initialValues?: Partial<RequestValues>
}

type ControlElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

type ResponseBody = {
    ok?: boolean
    reference?: string
    message?: string
    errors?: RequestErrors
}

const FALLBACK_MESSAGE = 'We could not send your request just now. Please try again in a moment, or message us on WhatsApp.'

const focusFirstInvalid = (form: HTMLFormElement): void => {
    window.requestAnimationFrame(() => {
        form.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
    })
}

export const useRequestForm = ({ type, formId, initialValues = {} }: UseRequestFormOptions) => {
    const [values, setValues] = useState<RequestValues>(() => emptyRequestValues(initialValues))
    const [errors, setErrors] = useState<RequestErrors>({})
    const [status, setStatus] = useState<RequestFormStatus>('idle')
    const [reference, setReference] = useState<string | null>(null)
    const [message, setMessage] = useState<string | null>(null)

    const setField = useCallback((field: RequestField, value: string): void => {
        setValues(previous => ({ ...previous, [field]: value }))
        setErrors(previous => {
            if (!previous[field]) return previous

            const next = { ...previous }

            delete next[field]

            return next
        })
    }, [])

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const form = event.currentTarget
        const nextErrors = validateRequest(type, values)

        setErrors(nextErrors)

        if (Object.keys(nextErrors).length) {
            setStatus('idle')
            setMessage(null)
            focusFirstInvalid(form)

            return
        }

        const honeypot = (form.elements.namedItem('company_website') as HTMLInputElement | null)?.value ?? ''

        setStatus('submitting')
        setMessage(null)

        try {
            const response = await fetch('/api/requests', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type, ...values, company_website: honeypot })
            })

            const data = await response.json().catch(() => null) as ResponseBody | null

            if (!response.ok || !data?.ok) {
                if (data?.errors) {
                    setErrors(data.errors)
                    focusFirstInvalid(form)
                }

                setStatus('error')
                setMessage(data?.message ?? FALLBACK_MESSAGE)

                return
            }

            setReference(data.reference ?? null)
            setStatus('success')
        }
        catch {
            setStatus('error')
            setMessage('We could not reach our kitchen just now. Please check your connection and try again, or message us on WhatsApp.')
        }
    }

    const reset = (): void => {
        setValues(emptyRequestValues(initialValues))
        setErrors({})
        setStatus('idle')
        setReference(null)
        setMessage(null)
    }

    const fieldProps = (field: RequestField) => ({
        id: `${formId}-${field}`,
        name: field,
        value: values[field],
        onChange: (event: ChangeEvent<ControlElement>) => setField(field, event.target.value),
        'aria-invalid': errors[field] ? true : undefined,
        'aria-describedby': errors[field] ? `${formId}-${field}-error` : undefined
    })

    return {
        values,
        errors,
        status,
        reference,
        message,
        setField,
        handleSubmit,
        reset,
        fieldProps
    }
}

// Today's date in the visitor's own time zone, filled in after mount so server and browser render the same markup.
export const useTodayDate = (): string => {
    const [today, setToday] = useState('')

    useEffect(() => {
        const now = new Date()
        const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000)

        setToday(local.toISOString().slice(0, 10))
    }, [])

    return today
}
